import { Alert, Input, Modal, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackGrowthEvent } from '../../utils/growthTracking';
import { FeedbackType, FeedbackValues, submitFeedback, validateFeedback } from '../../utils/feedback';
import './feedback.css';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  sourcePage: string;
}

const emptyValues: FeedbackValues = { type: 'usability', content: '', email: '' };

export default function FeedbackModal({ open, onClose, sourcePage }: FeedbackModalProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<FeedbackValues>(emptyValues);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'queued' | 'error'>('idle');
  const [errorField, setErrorField] = useState<Record<string, 'required' | 'invalid'>>({});

  useEffect(() => {
    if (open) {
      setStatus('idle');
      setErrorField({});
      trackGrowthEvent('feedback_open', sourcePage);
    }
  }, [open, sourcePage]);

  const update = (patch: Partial<FeedbackValues>) => {
    setValues(current => ({ ...current, ...patch }));
    setErrorField({});
  };

  const handleSubmit = async () => {
    const errors = validateFeedback(values);
    if (Object.keys(errors).length) {
      setErrorField(errors);
      return;
    }
    setSubmitting(true);
    try {
      const response = await submitFeedback(values, sourcePage);
      setStatus(response.delivery === 'queued' ? 'queued' : 'success');
      setValues(emptyValues);
      trackGrowthEvent('feedback_submit', sourcePage);
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const mailSubject = encodeURIComponent(`KiMap feedback - ${sourcePage}`);
  const mailBody = encodeURIComponent(values.content);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t('feedback.submit', 'Send feedback')}
      cancelText={t('feedback.cancel', 'Cancel')}
      confirmLoading={submitting}
      title={t('feedback.title', 'Help shape KiMap')}
      destroyOnHidden={false}
      className="feedback-modal"
    >
      <p className="feedback-modal-intro">{t('feedback.subtitle', 'Tell us what would make KiMap more useful for your work.')}</p>
      {status === 'success' && <Alert type="success" showIcon title={t('feedback.success', 'Thanks for helping shape KiMap.')} />}
      {status === 'queued' && <Alert type="success" showIcon title={t('feedback.queued', 'Feedback received. We will review it shortly.')} />}
      {status === 'error' && (
        <Alert
          type="warning"
          showIcon
          title={t('feedback.error', 'We could not send this right now. Your message is still here.')}
        />
      )}
      <Space orientation="vertical" size={14} className="feedback-form-fields">
        <label className="feedback-field-label" htmlFor="feedback-type">{t('feedback.typeLabel', 'Feedback type')}</label>
        <Select
          id="feedback-type"
          value={values.type}
          onChange={(type: FeedbackType) => update({ type })}
          options={[
            { value: 'bug', label: t('feedback.types.bug', 'Bug') },
            { value: 'feature', label: t('feedback.types.feature', 'Feature request') },
            { value: 'usability', label: t('feedback.types.usability', 'Usability') },
            { value: 'other', label: t('feedback.types.other', 'Other') },
          ]}
        />
        <label className="feedback-field-label" htmlFor="feedback-content">{t('feedback.contentLabel', 'Feedback')}</label>
        <Input.TextArea
          id="feedback-content"
          aria-label={t('feedback.contentLabel', 'Feedback')}
          rows={5}
          value={values.content}
          onChange={event => update({ content: event.target.value })}
          placeholder={t('feedback.contentPlaceholder', 'What happened, or what would you like to see?')}
          status={errorField.content ? 'error' : undefined}
        />
        {errorField.content && <span className="feedback-error">{t('feedback.contentRequired', 'Please enter your feedback.')}</span>}
        <label className="feedback-field-label" htmlFor="feedback-email">{t('feedback.emailLabel', 'Email (optional)')}</label>
        <Input
          id="feedback-email"
          type="email"
          value={values.email}
          onChange={event => update({ email: event.target.value })}
          placeholder={t('feedback.emailPlaceholder', 'Only if you would like a reply')}
          status={errorField.email ? 'error' : undefined}
        />
        {errorField.email && <span className="feedback-error">{t('feedback.emailInvalid', 'Please enter a valid email.')}</span>}
        <a className="feedback-direct-email" href={`mailto:kimap.founder@gmail.com?subject=${mailSubject}&body=${mailBody}`}>
          {t('feedback.emailFallback', 'Email us directly')}
        </a>
        <span className="feedback-privacy-note">{t('feedback.privacy', 'Your email is optional and is only used to reply to this feedback.')}</span>
      </Space>
    </Modal>
  );
}
